import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useRef, useState } from "react";

import IconInfo from "@/icons/icon-info";
import CartLayout from "~/components/cart/cart-layout";
import Icon from "~/components/common/icon";
import RichText from "~/components/common/rich-text";
import EditorialEntry from "~/components/editorials/editorial-entry";
import LayoutPage from "~/components/layout/layout-page";
import { usePageTracking } from "~/contexts/page-trackings";
import { useCartProductsHierarchicalData, useCartWithPendingStatus } from "~/hooks/use-cart";
import { cartRecommenderElegibleProduct } from "~/services/algolia-utils";
import styles from "~/styles/pages/cart.module.scss";
import Editorials from "~/types/editorials";
import { isActiveEntry } from "~/utils/editorial-utils";
import ssrUtils from "~/utils/ssr-utils";
import TrackingUtils from "~/utils/tracking-utils";

type Props = InferGetServerSidePropsType<typeof getServerSideProps>;

export default function CartPage(props: Props) {
  const { page, ...rest } = props;
  const t = useTranslations();
  const { cart } = useCartWithPendingStatus();
  const cartProductsParentsData = useCartProductsHierarchicalData();

  const { isPageTracked } = usePageTracking("cart", { title: props.page?.seoMetadata?.fields.seoTitle });
  const ecomTracked = useRef<boolean>(false);
  useEffect(() => {
    if (!ecomTracked.current && isPageTracked && cartProductsParentsData) {
      //@ts-ignore
      TrackingUtils.track(TrackingUtils.mapEcomCart("view_cart", cart?.ShoppingCart, cartProductsParentsData));
      ecomTracked.current = true;
    }
    //@ts-ignore
  }, [cart?.ShoppingCart, cartProductsParentsData, isPageTracked]);

  // Cart state is now managed by react-query, no manual refresh needed

  const isCartAlgoliaRelatedEnabled = useMemo(() => {
    const latestCartProduct = cartRecommenderElegibleProduct(cart);
    return !!latestCartProduct || false;
  }, [cart]);

  return (
    <LayoutPage {...rest} page={page as Editorials.BasicPageTemplate} navColor="gradient" hidePrefooter>
      <>
        <div className={styles.container}>
          <div className={styles.titleContainer}>
            <h1>{t("generic.your_cart", { quantity: (cart as any)?.ShoppingCart?.TotalItems ?? 0 })}</h1>
          </div>
          {props.page?.showInformativeMessage && props.page?.informativeMessage != undefined && (
            <div className={styles.message}>
              <Icon icon={IconInfo} width={24} height={24} />
              <RichText text={props.page.informativeMessage} />
            </div>
          )}
          <CartLayout page={page!} />
        </div>
        {isCartAlgoliaRelatedEnabled
          ? page?.fullCartComponents?.filter(isActiveEntry).map((entry) => {
              return <EditorialEntry key={entry.sys.id} entry={entry} />;
            })
          : page?.emptyCartComponents?.filter(isActiveEntry).map((entry) => {
              return <EditorialEntry key={entry.sys.id} entry={entry} />;
            })}
      </>
    </LayoutPage>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  if (context.locale === context.defaultLocale) {
    return {
      notFound: true,
    };
  }

  const pageData = await ssrUtils.getServerSideCartPageData(context);

  return {
    props: {
      ...pageData,
    },
  };
}
